import React, {createContext, useContext, useEffect, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {useAuthUser} from '../../@crema/utility/AuthHooks';
import {onlyUnique} from '../../utility/utility/common';
import {Symbol} from '../symbols/SymbolProvider';
import {
  DAY_TRADING_TICKET_TIMING_TYPE,
  MARKET_TRADING_TICKET_TYPE,
  MKT_ORDER_TYPE,
} from '../condition/types';

export type Condition = {
  id: string;
  name: string;
  condition: string;
  actionType: number;
  orderType: number;
  quantity: number;
};

export type EventType = {
  id: string;
  name: string;
};

type ConditionContextType = {
  conditions: Condition[];
  symbolQuotes: object[];
  symbolQuotesStandard: object[];
  conditionQuotes: string[];
  eventTypes: EventType[];
  condition: string;
  quantity: number | string | null;
  actionType: number | null;
  symbol: string | null;
  conditionStandardQuotes: string[];
  symbols: Symbol[];
  loading: boolean;
  isShowActive: boolean;
  orderType: number | null;
  tradingTicketType: number | null;
  tradingTicketTimingType: number | null;
  isAutoCorrectionLoading: boolean;
  similarConditions: object[];
  conditionError: string | null;
  conditionNameError: string | null;
  mark: number | null;
  contingentOrderType: number | null;
  isContingentOrder: boolean;
  isQuantityFromQuote: boolean;
  quantityFromQuoteId: string | null;
  quantityFromQuoteFieldId: string | null;
};

interface ConditionActionsContextType {
  create: Function;
  remove: Function;
  setConditionQuotes: Function;
  setConditionStandardQuotes: Function;
  setCondition: Function;
  setConditionError: Function;
  setConditionNameError: Function;
  setQuantity: Function;
  setSelectedSymbol: Function;
  setActionType: Function;
  switchActivity: Function;
  setOrderType: Function;
  setTradingTicketType: Function;
  setTradingTicketTimingType: Function;
  setIsAutoCorrectionLoading: Function;
  autoCorrectConditions: Function;
  fetchConditions: Function;
  setIsShowActive: Function;
  setMark: Function;
  setContingentOrderType: Function;
  setIsContingentOrder: Function;
  setIsQuantityFromQuote: Function;
  setQuantityFromQuoteId: Function;
  setQuantityFromQuoteFieldId: Function;
}

const defaultConditionContext = {
  conditions: [],
  symbolQuotes: [],
  symbolQuotesStandard: [],
  symbol: null,
  conditionQuotes: [],
  conditionStandardQuotes: [],
  eventTypes: [],
  loading: false,
  isShowActive: false,
  isAutoCorrectionLoading: false,
  condition: '',
  quantity: null,
  actionType: null,
  orderType: MKT_ORDER_TYPE,
  tradingTicketType: MARKET_TRADING_TICKET_TYPE,
  tradingTicketTimingType: DAY_TRADING_TICKET_TIMING_TYPE,
  symbols: [],
  similarConditions: [],
  conditionError: null,
  conditionNameError: null,
  mark: null,
  contingentOrderType: 1,
  isContingentOrder: false,
  isQuantityFromQuote: false,
  quantityFromQuoteId: null,
  quantityFromQuoteFieldId: null,
};

const ConditionContext = createContext<ConditionContextType>(
  defaultConditionContext,
);

const ConditionActionsContext = createContext<ConditionActionsContextType>({
  create: () => {},
  remove: () => {},
  setConditionQuotes: () => {},
  setConditionStandardQuotes: () => {},
  setCondition: () => {},
  setConditionError: () => {},
  setConditionNameError: () => {},
  setQuantity: () => {},
  setSelectedSymbol: () => {},
  setActionType: () => {},
  switchActivity: () => {},
  setOrderType: () => {},
  setTradingTicketType: () => {},
  setTradingTicketTimingType: () => {},
  setIsAutoCorrectionLoading: () => {},
  autoCorrectConditions: () => {},
  fetchConditions: () => {},
  setIsShowActive: () => {},
  setMark: () => {},
  setContingentOrderType: () => {},
  setIsContingentOrder: () => {},
  setIsQuantityFromQuote: () => {},
  setQuantityFromQuoteId: () => {},
  setQuantityFromQuoteFieldId: () => {},
});

export const useConditionContext = () => useContext(ConditionContext);
export const useConditionActionsContext = () =>
  useContext(ConditionActionsContext);

export const SHOW_ACTIVE_CONDITIONS = 'SHOW_ACTIVE_CONDITIONS';

const ConditionsProvider = ({children}: any) => {
  const {user} = useAuthUser();
  const isShowActiveFromStorage = window.localStorage.getItem(
    SHOW_ACTIVE_CONDITIONS,
  );

  const [conditions, setConditions] = useState(
    defaultConditionContext.conditions,
  );
  const [condition, setCondition] = useState(defaultConditionContext.condition);
  const [conditionError, setConditionError] = useState(
    defaultConditionContext.conditionError,
  );
  const [conditionNameError, setConditionNameError] = useState(
    defaultConditionContext.conditionNameError,
  );
  const [quantity, setQuantity] = useState(defaultConditionContext.quantity);
  const [symbol, setSymbol] = useState(defaultConditionContext.symbol);
  const [symbolQuotesStandard, setSymbolQuotesStandard] = useState(
    defaultConditionContext.symbolQuotesStandard,
  );
  const [actionType, setActionType] = useState(
    defaultConditionContext.actionType,
  );
  const [symbolQuotes, setSymbolQuotes] = useState(
    defaultConditionContext.symbolQuotes,
  );
  const [symbols, setSymbols] = useState(defaultConditionContext.symbols);
  const [eventTypes, setEventTypes] = useState(
    defaultConditionContext.eventTypes,
  );
  const [conditionQuotes, setConditionQuotes] = useState(
    defaultConditionContext.conditionQuotes,
  );
  const [orderType, setOrderType] = useState(defaultConditionContext.orderType);
  const [tradingTicketType, setTradingTicketType] = useState(
    defaultConditionContext.tradingTicketType,
  );
  const [tradingTicketTimingType, setTradingTicketTimingType] = useState(
    defaultConditionContext.tradingTicketTimingType,
  );
  const [conditionStandardQuotes, setConditionStandardQuotes] = useState(
    defaultConditionContext.conditionQuotes,
  );
  const [mark, setMark] = useState<number | null>(defaultConditionContext.mark);
  const [contingentOrderType, setContingentOrderType] = useState(
    defaultConditionContext.contingentOrderType,
  );
  const [isContingentOrder, setIsContingentOrder] = useState(
    defaultConditionContext.isContingentOrder,
  );
  const [isQuantityFromQuote, setIsQuantityFromQuote] = useState(
    defaultConditionContext.isQuantityFromQuote,
  );
  const [quantityFromQuoteId, setQuantityFromQuoteId] = useState(
    defaultConditionContext.quantityFromQuoteId,
  );
  const [quantityFromQuoteFieldId, setQuantityFromQuoteFieldId] = useState(
    defaultConditionContext.quantityFromQuoteFieldId,
  );
  const [loading, setLoading] = useState(defaultConditionContext.loading);

  const isShowActiveInitialValue =
    isShowActiveFromStorage === undefined || isShowActiveFromStorage === null
      ? defaultConditionContext.isShowActive
      : !!+isShowActiveFromStorage;
  const [isShowActive, setIsShowActive] = useState(isShowActiveInitialValue);

  const [isAutoCorrectionLoading, setIsAutoCorrectionLoading] = useState(
    defaultConditionContext.isAutoCorrectionLoading,
  );
  const [similarConditions, setSimilarConditions] = useState(
    defaultConditionContext.similarConditions,
  );

  useEffect(() => {
    fetchConditions();
  }, []);

  const fetchConditions = () => {
    setLoading(true);
    Promise.all([
      getConditions().then(({data}) => setConditions(data.data)),
      getSymbolQuotes().then(({data}) => setSymbolQuotes(data.data)),
      getSymbolQuotesStandard().then(({data}) =>
        setSymbolQuotesStandard(data.data),
      ),
      getEventTypes().then(({data}) => setEventTypes(data.data)),
      getSymbols().then(({data}) => setSymbols(data.data)),
    ])
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const create = (
    name: string,
    condition: string,
    isActive: boolean,
    eventTypes: string[],
    actionType: number | undefined | null,
    ordersLimit: number,
    tradingTicketType: number | undefined | null,
    tradingTicketTimingType: number | undefined | null,
    orderType: number | undefined | null,
    relatedCondition: string | null,
    quantity: number | null,
    mark: number | null,
    contingentOrderType: number | null,
    isContingentOrder: boolean,
    isQuantityFromQuote: boolean,
    quantityFromQuoteId: string | null,
    quantityFromQuoteFieldId: string | null,
  ) => {
    return new Promise((resolve, reject) => {
      createCondition(
        name,
        condition,
        isActive,
        ordersLimit,
        eventTypes,
        actionType ? actionType : null,
        tradingTicketType ? tradingTicketType : null,
        tradingTicketTimingType ? tradingTicketTimingType : null,
        orderType ? orderType : null,
        relatedCondition ? relatedCondition : null,
        quantity,
        conditionStandardQuotes,
        mark,
        contingentOrderType,
        isContingentOrder,
        isQuantityFromQuote,
        quantityFromQuoteId,
        quantityFromQuoteFieldId,
      )
        .then(() => {
          resolve(true);
        })
        .catch((e) => reject(e));
    });
  };

  const remove = (id: string) => {
    setLoading(true);
    deleteCondition(id).then(() => {
      getConditions().then(({data}) => {
        setConditions(data.data);
        setLoading(false);
      });
    });
  };

  const fetchSimiLarConditions = () => {
    getSimilarConditions()
      .then(({data}) => {
        setSimilarConditions(data.data);
      })
      .catch((e) => {
        console.log(e);
      });
  };

  useEffect(() => {
    if (actionType && symbol) {
      fetchSimiLarConditions();
    }
  }, [actionType, symbol]);

  const getConditions = async () => {
    return jwtAxios.get(`/condition/get-all/${user.id}`);
  };

  const getEventTypes = async () => {
    return jwtAxios.get(`/event-type/get-all`);
  };

  const getSymbolQuotesStandard = async () => {
    return jwtAxios.get(`/quote-standard/get-all`);
  };

  const getSymbolQuotes = async () => {
    return jwtAxios.get(`/symbol-quote/get-all/${user.id}`);
  };

  const getSimilarConditions = async () => {
    return jwtAxios.get(
      `/condition/get-similar/${user.id}/${actionType}/${symbol}`,
    );
  };

  const getSymbols = async () => {
    return jwtAxios.get(`/symbol/get-all/${user.id}`);
  };

  const switchActivity = async (id: string, isActive: boolean) => {
    return jwtAxios.patch(`/condition/update-activity`, {id, isActive});
  };

  const createCondition = async (
    name: string,
    condition: string,
    isActive: boolean,
    ordersLimit: number,
    eventTypes: string[],
    actionType: number | null,
    tradingTicketType: number | null,
    tradingTicketTimingType: number | null,
    orderType: number | null,
    relatedCondition: string | null,
    quantity: number | null,
    quotesStandard: string[],
    mark: number | null,
    contingentOrderType: number | null,
    isContingentOrder: boolean,
    isQuantityFromQuote: boolean,
    quantityFromQuote: string | null,
    quantityFromQuoteField: string | null,
  ) => {
    const conditionQuotesParam = conditionQuotes.filter(onlyUnique);
    return jwtAxios.post(`/condition/create/${user.id}`, {
      name,
      condition,
      isActive,
      ordersLimit,
      // @ts-ignore
      symbolId: symbol,
      quotes: conditionQuotesParam,
      eventTypes: eventTypes,
      actionType: actionType,
      tradingTicketType: tradingTicketType,
      tradingTicketTimingType: tradingTicketTimingType,
      orderType: orderType,
      relatedConditionId: relatedCondition,
      quantity,
      quotesStandard,
      mark,
      contingentOrderType,
      isContingentOrder,
      isQuantityFromQuote,
      quantityFromQuote,
      quantityFromQuoteField,
    });
  };

  const deleteCondition = async (id: string) => {
    return jwtAxios.delete(`/condition/remove/${id}`);
  };

  const setSelectedSymbol = (symbolId: string) => {
    // @ts-ignore
    setSymbol(symbolId);
    setCondition('');
    setQuantity(null);
    setQuantityFromQuoteId(null);
    setQuantityFromQuoteFieldId(null);
  };

  const autoCorrectConditions = async () => {
    return jwtAxios.post(`/condition/auto-correct/all`);
  };

  return (
    <ConditionContext.Provider
      value={{
        conditions,
        condition,
        symbolQuotes,
        conditionQuotes,
        symbol,
        eventTypes,
        actionType,
        symbolQuotesStandard,
        conditionStandardQuotes,
        loading,
        isShowActive,
        quantity,
        symbols,
        orderType,
        tradingTicketType,
        tradingTicketTimingType,
        isAutoCorrectionLoading,
        similarConditions,
        conditionError,
        conditionNameError,
        mark,
        contingentOrderType,
        isContingentOrder,
        isQuantityFromQuote,
        quantityFromQuoteId,
        quantityFromQuoteFieldId,
      }}>
      <ConditionActionsContext.Provider
        value={{
          create,
          remove,
          setConditionQuotes,
          setCondition,
          setQuantity,
          setSelectedSymbol,
          setConditionStandardQuotes,
          setActionType,
          switchActivity,
          setIsShowActive,
          setOrderType,
          setTradingTicketType,
          setTradingTicketTimingType,
          setIsAutoCorrectionLoading,
          autoCorrectConditions,
          fetchConditions,
          setConditionError,
          setConditionNameError,
          setMark,
          setContingentOrderType,
          setIsContingentOrder,
          setIsQuantityFromQuote,
          setQuantityFromQuoteId,
          setQuantityFromQuoteFieldId,
        }}>
        {children}
      </ConditionActionsContext.Provider>
    </ConditionContext.Provider>
  );
};

export default ConditionsProvider;
